import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useToast } from '../bpm';
import { useAuth } from '../../contexts/AuthContext';
import { useOliverName } from '../../contexts/OliverContext';
import { getAuthHeaders } from '../../services/api';
import { aiContextRegistry } from '../../ai/AIContextRegistry';
import { AIAliasRegistry } from '../../ai/AIAliasRegistry';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import './AIChat.css';

const PROVIDER_ALIAS_MAP = {
  claude: 'claude',
  gpt: 'openai',
  chatgpt: 'openai',
  gemini: 'gemini',
  grok: 'grok',
};

/** Couleurs des @mentions par IA (fixes, pas la couleur d'accent). */
const AT_MENTION_COLORS = {
  openai: '#10A37F',   // ChatGPT - vert profond
  claude: '#F97316',   // Claude - orange
  gemini: '#4285F4',   // Gemini - bleu
  grok: '#626262',     // Grok - gris
};

const PROVIDERS = [
  { id: 'claude', shortLabel: 'Claude', color: AT_MENTION_COLORS.claude, icon: '🟡', bgColor: 'rgba(249,115,22,0.1)' },
  { id: 'openai', shortLabel: 'GPT', color: AT_MENTION_COLORS.openai, icon: '🟢', bgColor: 'rgba(16,163,127,0.1)' },
  { id: 'gemini', shortLabel: 'Gemini', color: AT_MENTION_COLORS.gemini, icon: '🔵', bgColor: 'rgba(66,133,244,0.1)' },
  { id: 'grok', shortLabel: 'Grok', color: AT_MENTION_COLORS.grok, icon: '🔷', bgColor: 'rgba(98,98,98,0.1)' },
];

function parseProviderFromText(text, fallback) {
  const match = text.match(/@(\w+)/i);
  if (match) {
    const normalized = PROVIDER_ALIAS_MAP[match[1].toLowerCase()];
    if (normalized) return normalized;
  }
  return fallback;
}

/** Retourne la liste des providers @mentionnés dans le texte, dans l'ordre d'apparition (sans doublon). */
function parseAllProvidersFromText(text) {
  const seen = new Set();
  const order = [];
  const re = /@(\w+)/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const normalized = PROVIDER_ALIAS_MAP[m[1].toLowerCase()];
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      order.push(normalized);
    }
  }
  return order;
}

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

/** Lit un fichier en base64 (pour envoi au backend : extraction PDF côté serveur). Retourne { name, content_base64, mime_type } ou { name, error }. */
function readFileAsBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
        resolve({ name: file.name, error: true });
        return;
      }
      const base64 = dataUrl.replace(/^data:[^;]+;base64,/, '');
      resolve({ name: file.name, content_base64: base64, mime_type: file.type || null });
    };
    reader.onerror = () => resolve({ name: file.name, error: true });
    reader.readAsDataURL(file);
  });
}

/** Occurrences réelles $table / $doc / $chart dans l’app — liste affichée à l’autocomplétion */
const TABLE_OCCURRENCES = [
  { value: 'portefeuille', label: 'Portefeuille', location: 'Portefeuille / Détail du portefeuille' },
  { value: 'valorisations', label: 'Valeur de la position', location: 'Portefeuille / Valorisations' },
  { value: 'valeur_nette', label: 'Valeur nette', location: 'Compte / Valorisations' },
  { value: 'investisseurs', label: 'Investisseurs', location: 'Compte / Investisseurs' },
  { value: 'apports_retraits', label: 'Apports/Retraits', location: 'Compte / Apports/retraits' },
];
const DOC_OCCURRENCES = [
  { value: 'meta_analyse', label: 'Méta-analyse', location: 'Analyse / Analyse' },
  { value: 'rapports', label: 'Rapports', location: 'Analyse / Rapport' },
];
const CHART_OCCURRENCES = [
  { value: 'categorie', label: 'Catégorie', location: 'Portefeuille / Synthèse / Répartition par catégorie' },
  { value: 'fragilite', label: 'Fragilité', location: 'Portefeuille / Synthèse / Répartition par fragilité' },
  { value: 'evolution_position', label: 'Valeur de la position', location: 'Portefeuille / Synthèse / Evolution de la valeur du portefeuille' },
  { value: 'evolution_compte', label: 'Valeur nette', location: 'Compte / Performance / Evolution de la valeur du compte' },
];

/** Métriques (bpm.metric) — valeurs ponctuelles pour $metric:xxx */
const METRIC_OCCURRENCES = [
  { value: 'valorisation_totale', label: 'Valorisation totale', location: 'Dashboard / Portefeuille' },
  { value: 'nb_positions', label: 'Nombre de positions', location: 'Dashboard / Portefeuille' },
  { value: 'valeur_nette', label: 'Valeur nette (compte)', location: 'Compte' },
];

function filterOccurrences(list, paramLower) {
  if (!paramLower) return list;
  return list.filter(
    (o) =>
      o.label.toLowerCase().includes(paramLower) ||
      o.value.toLowerCase().startsWith(paramLower)
  );
}

const IA_TOAST_ICON = '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M489-384q51 0 88.5-33t37.5-81q0-40-25-68.5T527-595q-31 0-54 20.5T450-525q0 13 5.5 25t15.5 23l23-19q-8-6-12-13t-4-15q0-15 12.5-28.5T527-566q24 0 42 18.5t18 50.5q0 34-27 59.5T490-412q-48 0-83-38t-35-96q0-29 11.5-56t33.5-49l-21-20q-25 25-39 57t-14 67q0 67 42 115t104 48ZM292-132v-144q-57-52-88.5-115.5T172-521q0-128 90-217.5T480-828q103 0 187.5 63.5T777-601l38 152q4 14-5 25.5T786-412h-78v140q0 25-17.5 42.5T648-212H548v80h-28v-108h128q14 0 23-9t9-23v-168h108l-38-155q-23-92-99-148.5T480-800q-116 0-198 81.5T200-521q0 60 24.5 113.5T294-312l26 24v156h-28Zm202-334Z"/></svg>';

function isTokenError(message) {
  if (!message || typeof message !== 'string') return false;
  const m = message.toLowerCase();
  return /token\s*(invalide|expiré|invalid|expired)|invalide\s*ou\s*expiré|401|unauthorized/.test(m);
}

/** Erreur liée à la clé API du provider (OpenAI, Claude, etc.) → titre explicite pour Paramètres > API. */
function isProviderKeyError(message) {
  if (!message || typeof message !== 'string') return false;
  const m = message.toLowerCase();
  return (
    /api_key|api key|incorrect.*key|invalid.*key|authentication.*key|invalid_api_key|clé.*invalide/.test(m) ||
    /clé.*illisible|illisible.*clé|vérifiez.*paramètres.*api|oliver.*pas pu initialiser/.test(m)
  );
}

/** Provider non configuré (ex. "Provider 'openai' non configuré") → message pour Paramètres > API. */
function isProviderNotConfiguredError(message) {
  if (!message || typeof message !== 'string') return false;
  return /provider.*non configuré|non configuré/.test(message.toLowerCase());
}

/** Colore @mentions (IA), $tokens (contexte) et #tags (filtres sémantiques). */
function highlightAtAndDollar(text) {
  if (typeof text !== 'string') return text;
  // @provider | $token[:name][;dates] | #tag (pas de # au milieu d'un mot)
  const re = /(@[a-zA-Z0-9_.]+|\$[a-zA-Z]+(?::[^;\s]+)?(?:;[^\s]+)?|(?<!\w)#[a-zA-ZÀ-ÿ0-9_-]+)/g;
  const parts = text.split(re);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('@')) {
      const name = part.slice(1).toLowerCase();
      const providerId = PROVIDER_ALIAS_MAP[name];
      const color = providerId ? AT_MENTION_COLORS[providerId] : 'var(--accent-color, #A49B6D)';
      return (
        <span key={`ad-${i}`} className="bpm-ai-at-mention" data-provider={providerId || 'other'} style={{ color }}>{part}</span>
      );
    }
    if (part.startsWith('$')) {
      return (
        <span key={`ad-${i}`} className="bpm-ai-accent">{part}</span>
      );
    }
    if (part.startsWith('#')) {
      return (
        <span key={`ad-${i}`} className="bpm-ai-tag">{part}</span>
      );
    }
    return part;
  });
}

function MessageBubble({ message, providers, hideProviderLabel = false, oliverName = 'Oliver' }) {
  if (message.role === 'assistant') {
    const label = PROVIDERS.find((p) => p.id === message.provider)?.shortLabel || message.provider || oliverName;
    const color = PROVIDERS.find((p) => p.id === message.provider)?.color || AT_MENTION_COLORS[message.provider] || 'var(--text-secondary)';
    return (
      <div className="bpm-ai-message bpm-ai-message--assistant">
        {!hideProviderLabel && (
          <div className="bpm-ai-message-header">
            <span className="bpm-ai-message-provider" style={{ color }}>{label}:</span>
          </div>
        )}
        <div className="bpm-ai-message-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              text: ({ children }) => <>{highlightAtAndDollar(String(children))}</>,
            }}
          >
            {message.content || ''}
          </ReactMarkdown>
        </div>
      </div>
    );
  }
  const hasAttachments = message.hasAttachments === true;
  return (
    <div className="bpm-ai-message bpm-ai-message--user">
      <div className="bpm-ai-message-body">
        <>{highlightAtAndDollar(message.content || '')}</>
        {hasAttachments && (
          <span className="bpm-ai-message-attachment-icon" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M693.85-338.46q0 90.92-62.62 154.69Q568.62-120 478.46-120q-90.92 0-153.92-63.77-63-63.77-63-154.69v-349.23q0-63.46 43.65-107.89Q348.85-840 412.31-840q63.46 0 107.11 44.42 43.66 44.43 43.66 107.89v330.77q0 35.23-24.7 60.69-24.69 25.46-59.92 25.46-35.23 0-60.69-25.08-25.46-25.07-25.46-61.07v-332.31h40v332.31q0 19.15 13.11 32.65 13.12 13.5 32.27 13.5 19.16 0 32.27-13.5 13.12-13.5 13.12-32.65v-331.54q-.23-46.62-32.2-79.08Q458.92-800 412.31-800q-46.62 0-78.69 32.85-32.08 32.84-32.08 79.46v349.23q-.23 74.08 51.31 126.27Q404.38-160 478.46-160q73.08 0 124-52.19t51.39-126.27v-350.77h40v350.77Z"/>
            </svg>
          </span>
        )}
      </div>
    </div>
  );
}

export default function AIChat({ historyOpen = false, onCloseHistory, newDiscussionTrigger = 0 }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [activeProvider, setActiveProvider] = useState('claude');
  const [isStreaming, setIsStreaming] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState([]);
  const [providersLoadFailed, setProvidersLoadFailed] = useState(false);
  const [showProviderSugg, setShowProviderSugg] = useState(false);
  const [showTokenSugg, setShowTokenSugg] = useState(false);
  const [tokenSuggestions, setTokenSuggestions] = useState([]);
  const [portfolioSymbols, setPortfolioSymbols] = useState([]);
  /** Actifs du portefeuille avec symbole + nom (pour afficher le nom en gras dans les suggestions $actif:) */
  const [portfolioActifs, setPortfolioActifs] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const highlightRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const inputRowRef = useRef(null);
  const [tooltipWidth, setTooltipWidth] = useState(null);
  const [jwtRefreshTrigger, setJwtRefreshTrigger] = useState(0);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  /** Historique Oliver : liste des discussions (une ligne par fil d'échanges) et discussion sélectionnée */
  const [historyConversations, setHistoryConversations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  /** Id de la discussion en cours (fil d'échanges) ; null = nouvelle discussion */
  const [currentDiscussionId, setCurrentDiscussionId] = useState(null);
  /** Titres/descriptifs personnalisés par discussion (localStorage) */
  const [customTitles, setCustomTitles] = useState({});
  /** Afficher toutes les discussions (sinon 10 dernières, comme Notifications) */
  const [showAllHistory, setShowAllHistory] = useState(false);
  /** Animation dock : fermeture en cours (on garde le panneau monté jusqu'à la fin de l'animation) */
  const [historyClosing, setHistoryClosing] = useState(false);
  const [historyOpenAnimationActive, setHistoryOpenAnimationActive] = useState(false);
  const historyPanelRef = React.useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return;
    const handler = () => setKeyboardOffset(Math.max(0, window.innerHeight - window.visualViewport.height));
    window.visualViewport.addEventListener('resize', handler);
    window.visualViewport.addEventListener('scroll', handler);
    handler();
    return () => {
      window.visualViewport.removeEventListener('resize', handler);
      window.visualViewport.removeEventListener('scroll', handler);
    };
  }, [isMobile]);

  // Animation dock : lancer l'ouverture au frame suivant
  useEffect(() => {
    if (historyOpen && !historyClosing) {
      const id = requestAnimationFrame(() => setHistoryOpenAnimationActive(true));
      return () => cancelAnimationFrame(id);
    }
    if (!historyOpen) setHistoryOpenAnimationActive(false);
  }, [historyOpen, historyClosing]);

  const handleHistoryPanelAnimationEnd = (e) => {
    if (e.animationName === 'history-dock-close') {
      onCloseHistory?.();
      setHistoryClosing(false);
    }
  };

  const requestCloseHistory = () => {
    if (historyOpen && !historyClosing) setHistoryClosing(true);
  };

  // Nouvelle discussion déclenchée depuis le header (icône à gauche de l'historique)
  useEffect(() => {
    if (newDiscussionTrigger > 0) {
      setMessages([]);
      setCurrentDiscussionId(null);
      setAttachedFiles([]);
    }
  }, [newDiscussionTrigger]);

  const MAX_ATTACHED = 5;
  const MAX_FILE_SIZE_MB = 10;

  const addFiles = (fileList) => {
    if (!fileList?.length) return;
    const arr = Array.from(fileList);
    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
    const valid = arr.filter((f) => f.size <= maxBytes);
    if (valid.length < arr.length) {
      showToast(`Certains fichiers dépassent ${MAX_FILE_SIZE_MB} Mo et ont été ignorés.`, 'warning', 4000, 'Fichiers', oliverName, IA_TOAST_ICON);
    }
    setAttachedFiles((prev) => [...prev, ...valid].slice(0, MAX_ATTACHED));
    if (valid.length + (attachedFiles?.length || 0) > MAX_ATTACHED) {
      showToast(`Maximum ${MAX_ATTACHED} pièces jointes.`, 'info', 3000, oliverName, oliverName, IA_TOAST_ICON);
    }
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOver) setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const files = e.dataTransfer?.files;
    if (files?.length) addFiles(files);
  };
  const { showToast } = useToast();
  const { isAuthenticated, sessionCheckDone, appJwtReady } = useAuth();
  const { oliverName } = useOliverName();

  // Réessayer providers/positions quand le JWT est rafraîchi (ex. retour sur l'onglet)
  useEffect(() => {
    if (!sessionCheckDone || !isAuthenticated) return;
    const onJwtUpdated = () => setJwtRefreshTrigger((c) => c + 1);
    window.addEventListener('appJwtUpdated', onJwtUpdated);
    return () => window.removeEventListener('appJwtUpdated', onJwtUpdated);
  }, [sessionCheckDone, isAuthenticated]);

  // Charger l'historique des conversations quand le panneau s'ouvre
  useEffect(() => {
    if (!historyOpen || !isAuthenticated) return;
    setHistoryLoading(true);
    setSelectedHistoryId(null);
    const headers = getAuthHeaders();
    fetch(`${API_BASE}/ai/conversations?limit=100`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setHistoryConversations(list);
        try {
          const raw = localStorage.getItem('oliver_discussion_titles');
          setCustomTitles(raw ? JSON.parse(raw) : {});
        } catch (e) {
          setCustomTitles({});
        }
      })
      .catch(() => setHistoryConversations([]))
      .finally(() => setHistoryLoading(false));
  }, [historyOpen, isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* Garder l’overlay de surlignage @/$ en phase avec le scroll du textarea */
  useEffect(() => {
    if (inputText && highlightRef.current && inputRef.current) {
      highlightRef.current.scrollTop = inputRef.current.scrollTop;
      highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  }, [inputText]);

  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const minH = 70;
    const maxH = 200;
    const h = Math.min(maxH, Math.max(minH, ta.scrollHeight));
    ta.style.height = `${h}px`;
  }, [inputText]);

  // Largeur du tooltip = largeur de la ligne du champ (input + boutons) pour alignement visuel
  useEffect(() => {
    if (!showProviderSugg && !showTokenSugg) {
      setTooltipWidth(null);
      return;
    }
    const measure = () => {
      if (inputRowRef.current) {
        setTooltipWidth(inputRowRef.current.getBoundingClientRect().width);
      }
    };
    let ro = null;
    const id = requestAnimationFrame(() => {
      measure();
      if (typeof ResizeObserver !== 'undefined' && inputRowRef.current) {
        ro = new ResizeObserver(measure);
        ro.observe(inputRowRef.current);
      }
    });
    return () => {
      cancelAnimationFrame(id);
      if (ro) ro.disconnect();
    };
  }, [showProviderSugg, showTokenSugg]);

  useEffect(() => {
    if (!sessionCheckDone || !isAuthenticated || !appJwtReady) return;
    setProvidersLoadFailed(false);
    const doFetch = (isRetry) => {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        console.warn('[Oliver] Chargement providers: pas de token (getAuthHeaders vide)');
        setProvidersLoadFailed(true);
        return Promise.resolve();
      }
      return fetch(`${API_BASE}/ai/providers`, { headers })
        .then((r) => {
          if (r.status === 401 && !isRetry) {
            return new Promise((resolve) => {
              setTimeout(() => {
                const h = getAuthHeaders();
                if (h.Authorization) resolve(fetch(`${API_BASE}/ai/providers`, { headers: h }));
                else resolve(r);
              }, 800);
            });
          }
          return r;
        })
        .then((r) => (r && r.ok ? r.json() : Promise.reject()));
    };
    const PROVIDERS_TIMEOUT_MS = 8000;
    const timeoutId = setTimeout(() => setProvidersLoadFailed(true), PROVIDERS_TIMEOUT_MS);
    const clearLoadTimeout = () => { clearTimeout(timeoutId); };

    doFetch(false)
      .then((data) => {
        clearLoadTimeout();
        const raw = data?.providers ?? (Array.isArray(data) ? data : null);
        const list = Array.isArray(raw) ? raw : [];
        setConfiguredProviders(list);
        setProvidersLoadFailed(false);
        const defaultP = typeof data?.default_provider === 'string' ? data.default_provider : null;
        const first = list.find((p) => p && p.is_configured && p.is_active);
        if (defaultP && list.some((p) => p && p.provider_name === defaultP && p.is_configured && p.is_active)) {
          setActiveProvider(defaultP);
        } else if (first && first.provider_name) {
          setActiveProvider(first.provider_name);
        }
      })
      .catch(() => {
        clearLoadTimeout();
        setProvidersLoadFailed(true);
      });
  }, [sessionCheckDone, isAuthenticated, appJwtReady, jwtRefreshTrigger]);

  useEffect(() => {
    if (!sessionCheckDone || !isAuthenticated || !appJwtReady) return;
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;

    const applyPositions = (positions) => {
      const list = positions?.positions || positions || [];
      const syms = list.map((p) => p.symbol || p.symbole).filter(Boolean);
      setPortfolioSymbols([...new Set(syms)]);
      const actifs = list
        .filter((p) => p.symbol || p.symbole)
        .map((p) => ({ symbol: (p.symbol || p.symbole).toUpperCase(), name: p.name || p.nom || p.symbol || p.symbole }));
      const bySymbol = new Map();
      actifs.forEach((a) => bySymbol.set(a.symbol, a));
      const sorted = Array.from(bySymbol.values()).sort((a, b) => (a.symbol || '').localeCompare(b.symbol || '', undefined, { sensitivity: 'base' }));
      setPortfolioActifs(sorted);
    };

    fetch(`${API_BASE}/tws/positions`, { headers })
      .then((r) => {
        if (r.ok) return r.json().then(applyPositions);
        if (r.status === 503) {
          return fetch(`${API_BASE}/ai/portfolio-symbols`, { headers })
            .then((res) => (res.ok ? res.json() : { positions: [] }))
            .then(applyPositions);
        }
        return Promise.reject();
      })
      .catch(() => {
        fetch(`${API_BASE}/ai/portfolio-symbols`, { headers })
          .then((res) => (res.ok ? res.json() : { positions: [] }))
          .then(applyPositions)
          .catch(() => {});
      });
  }, [sessionCheckDone, isAuthenticated, appJwtReady, jwtRefreshTrigger]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || isStreaming) return;

    const authHeaders = getAuthHeaders();
    let providers = parseAllProvidersFromText(text);
    if (providers.length === 0) providers = [parseProviderFromText(text, activeProvider)];
    const detectedTokens = aiContextRegistry.detectTokens(text);
    const messageToSend = text;
    const filesToSend = attachedFiles?.length ? [...attachedFiles] : [];

    /** Masquer immédiatement la zone de saisie (éviter la div avec bordure pendant le spinner). Ne pas vider les PJ : elles restent pour tout l'échange (ex. "Claude analyse ce fichier" puis "GPT qu'en penses-tu ?" → GPT a aussi accès au fichier). */
    setInputText('');
    setIsStreaming(true);
    abortRef.current = new AbortController();

    /** Pièces jointes : envoyées en base64 au backend (extraction PDF / décode texte côté serveur) */
    let attachmentsToSend = [];
    if (filesToSend.length > 0) {
      try {
        const results = await Promise.all(filesToSend.map(readFileAsBase64));
        attachmentsToSend = results
          .filter((r) => !r.error && r.content_base64 != null)
          .map((r) => ({ filename: r.name, content_base64: r.content_base64, mime_type: r.mime_type || undefined }));
        const failed = results.filter((r) => r.error);
        if (failed.length > 0) {
          showToast(`${failed.length} fichier(s) n'ont pas pu être envoyés.`, 'warning', 4000, 'Fichiers', oliverName, IA_TOAST_ICON);
        }
      } catch (e) {
        showToast('Erreur lors de la lecture des fichiers joints.', 'warning', 4000, 'Fichiers', oliverName, IA_TOAST_ICON);
        setIsStreaming(false);
        abortRef.current = null;
        return;
      }
    }

    const baseHistory = messages.slice(-10).map((m) => ({ role: m.role, content: m.content || '', provider: m.provider || null }));
    const userMsg = { role: 'user', content: text, provider: providers[0], tokens: detectedTokens, hasAttachments: attachmentsToSend.length > 0 };
    const removeUserAndAllAssistants = (n) => setMessages((prev) => prev.slice(0, -(1 + n)));

    if (providers.length === 1) {
      const providerName = providers[0];
      setMessages((prev) => [...prev, userMsg, { role: 'assistant', content: '', provider: providerName }]);
      try {
        const response = await fetch(`${API_BASE}/ai/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            message: messageToSend,
            provider_name: providerName,
            conversation_history: [...baseHistory, { role: 'user', content: messageToSend, provider: null }],
            discussion_id: currentDiscussionId || undefined,
            ...(attachmentsToSend.length > 0 ? { attachments: attachmentsToSend } : {}),
          }),
          signal: abortRef.current.signal,
        });
        if (response.status === 401) {
          showToast('Erreur d\'authentification.', 'error', 5000, 'Erreur d\'authentification.', oliverName, IA_TOAST_ICON);
          setIsStreaming(false);
          removeUserAndAllAssistants(1);
          return;
        }
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const msg = typeof err.detail === 'string' ? err.detail : (Array.isArray(err.detail) ? (err.detail[0]?.msg || err.detail[0]?.detail || String(err.detail[0] || '')) : (err.detail?.msg || err.detail?.detail || 'Erreur'));
          if (isTokenError(msg)) showToast(msg || 'Erreur d\'authentification.', 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
          else if (isProviderKeyError(msg)) showToast(msg, 'error', 8000, `Clé API ${providerName === 'openai' ? 'GPT' : providerName} invalide`, oliverName, IA_TOAST_ICON);
          else if (isProviderNotConfiguredError(msg)) showToast(`${msg} Configurez le provider dans Paramètres > API.`, 'error', 8000, 'Provider non configuré', oliverName, IA_TOAST_ICON);
          else showToast(msg, 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
          setIsStreaming(false);
          removeUserAndAllAssistants(1);
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const line of parts) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'chunk') setMessages((prev) => { const next = [...prev]; const last = next[next.length - 1]; if (last?.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + (event.t || '') }; return next; });
              else if (event.type === 'done') {
                if (event.discussion_id) setCurrentDiscussionId(event.discussion_id);
                if (event.context_injected) setMessages((prev) => { const next = [...prev]; const last = next[next.length - 1]; if (last?.role === 'assistant') next[next.length - 1] = { ...last, contextInjected: true }; return next; });
              } else if (event.type === 'error') {
                const msg = event.message || 'Erreur';
                if (isProviderKeyError(msg)) showToast('Vérifiez la clé API dans Paramètres > API.', 'error', 8000, 'Erreur', oliverName, IA_TOAST_ICON);
                else if (isTokenError(msg)) showToast(msg, 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
                else showToast(msg, 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
              }
            } catch (e) {}
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') showToast('Erreur de connexion', 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
        removeUserAndAllAssistants(1);
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
      return;
    }

    const baseIdx = messages.length + 1;
    setMessages((prev) => [...prev, userMsg, ...providers.map((p) => ({ role: 'assistant', content: '', provider: p }))]);
    let chainDiscussionId = currentDiscussionId;
    const accumulated = [];

    for (let i = 0; i < providers.length; i++) {
      const providerName = providers[i];
      const historyForRequest = [
        ...baseHistory,
        { role: 'user', content: messageToSend, provider: null },
        ...accumulated.map((content, j) => ({ role: 'assistant', content, provider: providers[j] })),
      ];
      try {
        const response = await fetch(`${API_BASE}/ai/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            message: messageToSend,
            provider_name: providerName,
            conversation_history: historyForRequest,
            discussion_id: chainDiscussionId || undefined,
            ...(attachmentsToSend.length > 0 ? { attachments: attachmentsToSend } : {}),
          }),
          signal: abortRef.current.signal,
        });
        if (response.status === 401) {
          showToast('Erreur d\'authentification.', 'error', 5000, 'Erreur', oliverName, IA_TOAST_ICON);
          setMessages((prev) => prev.slice(0, -(1 + providers.length)));
          break;
        }
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const msg = typeof err.detail === 'string' ? err.detail : (err.detail?.msg || err.detail?.detail || 'Erreur');
          const label = providerName === 'openai' ? 'GPT' : (providerName === 'claude' ? 'Claude' : providerName);
          if (isProviderKeyError(msg)) showToast(msg, 'error', 8000, `Clé API ${label} invalide`, oliverName, IA_TOAST_ICON);
          else if (isProviderNotConfiguredError(msg)) showToast(`${msg} Configurez ${label} dans Paramètres > API.`, 'error', 8000, 'Provider non configuré', oliverName, IA_TOAST_ICON);
          else showToast(msg, 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
          setMessages((prev) => prev.slice(0, -(1 + providers.length)));
          break;
        }
        let fullText = '';
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          const idx = baseIdx + i;
          for (const line of parts) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'chunk') {
                const t = event.t || '';
                fullText += t;
                setMessages((prev) => {
                  const next = [...prev];
                  if (next[idx]) next[idx] = { ...next[idx], content: next[idx].content + t };
                  return next;
                });
              } else if (event.type === 'done') {
                if (event.discussion_id) { chainDiscussionId = event.discussion_id; setCurrentDiscussionId(chainDiscussionId); }
                if (event.context_injected) setMessages((prev) => { const next = [...prev]; if (next[idx]) next[idx] = { ...next[idx], contextInjected: true }; return next; });
              } else if (event.type === 'error') {
                const msg = event.message || 'Erreur';
                if (isProviderKeyError(msg)) showToast('Vérifiez la clé API dans Paramètres > API.', 'error', 8000, 'Erreur', oliverName, IA_TOAST_ICON);
                else showToast(msg, 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
              }
            } catch (e) {}
          }
        }
        accumulated.push(fullText);
      } catch (e) {
        if (e.name !== 'AbortError') showToast('Erreur de connexion', 'error', 6000, 'Erreur', oliverName, IA_TOAST_ICON);
        setMessages((prev) => prev.slice(0, -(1 + providers.length)));
        break;
      }
    }
    setIsStreaming(false);
    abortRef.current = null;
  };

  const handleAbort = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      showToast('Génération annulée', 'info', 4000, 'Annulé', oliverName, IA_TOAST_ICON);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    const atMatch = value.match(/@(\w*)$/);
    setShowProviderSugg(!!atMatch);
    // $token ou $token:param (ex. $actif: ou $actif:PA pour actifs du portefeuille)
      const dollarMatch = value.match(/\$(\w*)(?::(\w*))?$/);
    if (dollarMatch) {
      const tokenPrefix = dollarMatch[1];
      const paramPrefix = dollarMatch[2];
      const legacySuggestions = aiContextRegistry.getSuggestions(tokenPrefix, portfolioActifs);
      const frameworkTokens = AIAliasRegistry.getInstance()
        .getAllTokens()
        .filter((t) => (t.token.replace('$', '') || '').toLowerCase().startsWith((tokenPrefix || '').toLowerCase()))
        .map((t) => ({ token: t.token, label: t.label || t.token, description: t.description, icon: t.icon }));
      const seenTokens = new Set(legacySuggestions.map((s) => s.token));
      const frameworkOnly = frameworkTokens.filter((t) => {
        if (seenTokens.has(t.token)) return false;
        seenTokens.add(t.token);
        return true;
      });
      // Framework ($table, $doc, $chart, $data, $actif, $broker) en premier pour être visibles
      const suggestions = [...frameworkOnly, ...legacySuggestions];
      if (paramPrefix !== undefined) {
        const paramLower = (paramPrefix || '').toLowerCase();
        const tokenLower = tokenPrefix.toLowerCase();
        if (tokenLower === 'table') {
          const filtered = filterOccurrences(TABLE_OCCURRENCES, paramLower);
          setTokenSuggestions(
            filtered.map((o) => ({
              token: `$table:${o.value}`,
              label: o.label,
              location: o.location,
              isSub: true,
            }))
          );
          setShowTokenSugg(filtered.length > 0);
        } else if (tokenLower === 'doc') {
          const filtered = filterOccurrences(DOC_OCCURRENCES, paramLower);
          setTokenSuggestions(
            filtered.map((o) => ({
              token: `$doc:${o.value}`,
              label: o.label,
              location: o.location,
              isSub: true,
            }))
          );
          setShowTokenSugg(filtered.length > 0);
        } else if (tokenLower === 'chart') {
          const filtered = filterOccurrences(CHART_OCCURRENCES, paramLower);
          setTokenSuggestions(
            filtered.map((o) => ({
              token: `$chart:${o.value}`,
              label: o.label,
              location: o.location,
              isSub: true,
            }))
          );
          setShowTokenSugg(filtered.length > 0);
        } else if (tokenLower === 'metric') {
          const filtered = filterOccurrences(METRIC_OCCURRENCES, paramLower);
          setTokenSuggestions(
            filtered.map((o) => ({
              token: `$metric:${o.value}`,
              label: o.label,
              location: o.location,
              isSub: true,
            }))
          );
          setShowTokenSugg(filtered.length > 0);
        } else {
          // Utilisateur a tapé ":" → proposer les actifs du portefeuille (ex. $actif:PA)
          const actifCtx = suggestions.find((s) => s.parameterized && s.subSuggestions?.length);
          const sub = actifCtx?.subSuggestions ?? [];
          const filtered = paramLower
            ? sub.filter((s) => (s.symbol || (s.token && s.token.split(':')[1]) || '').toLowerCase().startsWith(paramLower))
            : sub;
          const sortedFiltered = [...filtered].sort((a, b) => {
            const sa = (a.symbol || (a.token && a.token.split(':')[1]) || '').toLowerCase();
            const sb = (b.symbol || (b.token && b.token.split(':')[1]) || '').toLowerCase();
            return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
          });
          setTokenSuggestions(sortedFiltered.map((s) => ({ token: s.token, label: s.label, isSub: true })));
          setShowTokenSugg(sortedFiltered.length > 0);
        }
      } else {
        setTokenSuggestions(suggestions);
        setShowTokenSugg(suggestions.length > 0);
      }
    } else {
      setShowTokenSugg(false);
    }
  };

  const safeProviders = Array.isArray(configuredProviders) ? configuredProviders : [];
  const providerList = safeProviders.length > 0 ? safeProviders : PROVIDERS;

  /** Pour le @ : afficher tous les providers (sans filtrer par is_configured). Si non configuré, le toast s'affichera à l'envoi. */
  const atProviderSuggestions = (providerList || []).map((p) => ({
    id: p.provider_name || p.id,
    shortLabel: p.label || (PROVIDERS.find((pr) => pr.id === (p.provider_name || p.id))?.shortLabel) || p.provider_name || p.id,
    color: p.color,
    icon: p.icon,
  }));

  const atPrefix = (inputText.match(/@(\w*)$/) || [])[1] || '';
  const atFilteredList = atPrefix
    ? atProviderSuggestions.filter((p) => (p.shortLabel || '').toLowerCase().startsWith(atPrefix.toLowerCase()))
    : atProviderSuggestions;
  const hasProviderSugg = showProviderSugg && atFilteredList.length > 0;
  const showProviderTooltipMessage = showProviderSugg && atFilteredList.length === 0 && isAuthenticated;

  const selectedConversation = selectedHistoryId
    ? historyConversations.find((c) => c.id === selectedHistoryId)
    : null;
  const providerLabel = (name) => PROVIDERS.find((p) => p.id === name)?.shortLabel || name;
  const providerColor = (name) => PROVIDERS.find((p) => p.id === name)?.color || AT_MENTION_COLORS[name] || 'var(--text-secondary)';

  return (
    <div
      className="bpm-ai-chat"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {(historyOpen || historyClosing) && (
        <div
          className="bpm-ai-history-overlay"
          role="dialog"
          aria-label={`Historique des échanges avec ${oliverName}`}
          onClick={(e) => { if (e.target === e.currentTarget) requestCloseHistory(); }}
        >
          <div
            ref={historyPanelRef}
            className={`bpm-ai-history-panel${historyOpenAnimationActive ? ' tooltip-dock-open' : ''}${historyClosing ? ' tooltip-dock-closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onAnimationEnd={handleHistoryPanelAnimationEnd}
          >
            <div className="bpm-ai-history-header">
              <h2 className="bpm-ai-history-title">Historique des échanges</h2>
              {historyConversations.length > 0 && (
                <Tooltip title="Effacer l'historique" placement="bottom">
                  <button
                    type="button"
                    className="bpm-ai-history-clear-button"
                    onClick={() => {
                      if (!window.confirm(`Voulez-vous effacer tout l'historique des échanges avec ${oliverName} ?`)) return;
                      const headers = getAuthHeaders();
                      Promise.all(historyConversations.map((d) => fetch(`${API_BASE}/ai/conversations/${encodeURIComponent(d.id)}`, { method: 'DELETE', headers })))
                        .then(() => {
                          setHistoryConversations([]);
                          setSelectedHistoryId(null);
                          setShowAllHistory(false);
                          setCurrentDiscussionId(null);
                          setAttachedFiles([]);
                        })
                        .catch(() => {});
                    }}
                  >
                    Effacer
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="bpm-ai-history-body">
              {historyLoading ? (
                <p className="bpm-ai-history-loading">Chargement…</p>
              ) : historyConversations.length === 0 ? (
                <p className="bpm-ai-history-empty">Aucun échange enregistré.</p>
              ) : selectedConversation ? (
                <>
                  <button
                    type="button"
                    className="bpm-ai-history-back"
                    onClick={() => setSelectedHistoryId(null)}
                  >
                    ← Liste
                  </button>
                  <div className="bpm-ai-history-detail">
                    {(selectedConversation.messages || []).flatMap((msg, idx) => [
                      <MessageBubble key={`${idx}-user`} message={{ role: 'user', content: msg.user_message || '', provider: msg.provider_name }} providers={PROVIDERS} oliverName={oliverName} />,
                      <MessageBubble key={`${idx}-assistant`} message={{ role: 'assistant', content: msg.ai_response || '', provider: msg.provider_name }} providers={PROVIDERS} oliverName={oliverName} />,
                    ])}
                  </div>
                </>
              ) : (
                <>
                <ul className="bpm-ai-history-list">
                  {(showAllHistory ? historyConversations : historyConversations.slice(0, 10)).map((d) => {
                    const date = d.updated_at ? new Date(d.updated_at) : (d.created_at ? new Date(d.created_at) : null);
                    const dateStr = date ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }) : '';
                    const displayPreview = (customTitles[d.id] !== undefined && customTitles[d.id] !== '') ? customTitles[d.id] : (d.preview || '');
                    const msgCount = (d.messages || []).length;
                    const firstProvider = d.messages?.[0]?.provider_name;
                    return (
                      <li key={d.id} className="bpm-ai-history-row">
                        <button
                          type="button"
                          className="bpm-ai-history-item"
                          onClick={() => {
                            const msgs = (d.messages || []).flatMap((m) => [
                              { role: 'user', content: m.user_message || '', provider: m.provider_name },
                              { role: 'assistant', content: m.ai_response || '', provider: m.provider_name },
                            ]);
                            setMessages(msgs);
                            setCurrentDiscussionId(d.id);
                            setAttachedFiles([]);
                            setSelectedHistoryId(null);
                            requestCloseHistory();
                          }}
                        >
                          <span className="bpm-ai-history-item-meta">
                            {firstProvider && (
                              <>
                                <span className="bpm-ai-history-item-provider" style={{ color: providerColor(firstProvider), fontWeight: 'bold' }}>{providerLabel(firstProvider)}</span>
                                {' · '}
                              </>
                            )}
                            {msgCount > 0 && <span className="bpm-ai-history-item-count">{msgCount} message{msgCount > 1 ? 's' : ''}</span>}
                            {msgCount > 0 && ' · '}
                            {dateStr}
                          </span>
                          <span className="bpm-ai-history-item-preview">{displayPreview ? displayPreview.charAt(0).toUpperCase() + displayPreview.slice(1) : ''}</span>
                        </button>
                        <button
                          type="button"
                          className="bpm-ai-history-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = (customTitles[d.id] !== undefined && customTitles[d.id] !== '') ? customTitles[d.id] : (d.preview || '');
                            const value = window.prompt('Modifier le titre / descriptif de la conversation', current);
                            if (value !== null) {
                              const next = { ...customTitles, [d.id]: value.trim() };
                              setCustomTitles(next);
                              try {
                                localStorage.setItem('oliver_discussion_titles', JSON.stringify(next));
                              } catch (err) {}
                            }
                          }}
                          aria-label="Modifier le titre de la conversation"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden><path d="M200-200h43.92l427.93-427.92-43.93-43.93L200-243.92V-200Zm-40 40v-100.77l527.23-527.77q6.15-5.48 13.57-8.47 7.43-2.99 15.49-2.99t15.62 2.54q7.55 2.54 13.94 9.15l42.69 42.93q6.61 6.38 9.04 14 2.42 7.63 2.42 15.25 0 8.13-2.74 15.56-2.74 7.42-8.72 13.57L260.77-160H160Zm600.77-556.31-44.46-44.46 44.46 44.46ZM649.5-649.5l-21.58-22.35 43.93 43.93-22.35-21.58Z"/></svg>
                        </button>
                        <button
                          type="button"
                          className="bpm-ai-history-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!window.confirm('Supprimer cette discussion de l\'historique ?')) return;
                            const headers = getAuthHeaders();
                            fetch(`${API_BASE}/ai/conversations/${encodeURIComponent(d.id)}`, { method: 'DELETE', headers })
                              .then((res) => {
                                if (res.status === 204 || res.ok) {
                                  setHistoryConversations((prev) => prev.filter((conv) => conv.id !== d.id));
                                  if (selectedHistoryId === d.id) setSelectedHistoryId(null);
                                  if (currentDiscussionId === d.id) {
                                    setCurrentDiscussionId(null);
                                    setAttachedFiles([]);
                                  }
                                  setCustomTitles((prev) => {
                                    const next = { ...prev };
                                    delete next[d.id];
                                    try {
                                      localStorage.setItem('oliver_discussion_titles', JSON.stringify(next));
                                    } catch (err) {}
                                    return next;
                                  });
                                }
                              })
                              .catch(() => {});
                          }}
                          aria-label="Supprimer cette discussion"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden><path d="M304.62-160q-26.85 0-45.74-18.88Q240-197.77 240-224.62V-720h-40v-40h160v-30.77h240V-760h160v40h-40v495.38q0 27.62-18.5 46.12Q683-160 655.38-160H304.62ZM680-720H280v495.38q0 10.77 6.92 17.7 6.93 6.92 17.7 6.92h350.76q9.24 0 16.93-7.69 7.69-7.69 7.69-16.93V-720ZM392.31-280h40v-360h-40v360Zm135.38 0h40v-360h-40v360ZM280-720v520-520Z"/></svg>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {historyConversations.length > 10 && !showAllHistory && (
                  <div className="bpm-ai-history-show-more">
                    <button
                      type="button"
                      className="bpm-ai-history-show-more-button"
                      onClick={() => setShowAllHistory(true)}
                    >
                      Afficher toutes les discussions ({historyConversations.length})
                    </button>
                  </div>
                )}
                {showAllHistory && historyConversations.length > 10 && (
                  <div className="bpm-ai-history-show-less">
                    <button
                      type="button"
                      className="bpm-ai-history-show-less-button"
                      onClick={() => setShowAllHistory(false)}
                    >
                      Afficher moins
                    </button>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {isDraggingOver && (
        <div className="bpm-ai-chat-drag-overlay" aria-hidden>
          <span className="bpm-ai-chat-drag-overlay-text">Déposez les fichiers ici</span>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="bpm-ai-chat-file-input-hidden"
        accept="*/*"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
        aria-label="Joindre des fichiers"
      />
      <div className="bpm-ai-chat-messages">
        {messages.length === 0 && (
          <div className="bpm-ai-chat-welcome">
            {!sessionCheckDone ? (
              <p><strong>Chargement…</strong></p>
            ) : !isAuthenticated ? (
              <p><strong>Connectez-vous pour discuter avec {oliverName}.</strong></p>
            ) : (
              <>
                <p><strong>Discuter avec {oliverName}…</strong></p>
                <p><code>@</code> Modèle : <code>@Claude</code>, <code>@GPT</code>, <code>@Gemini</code>, <code>@Grok</code>.</p>
                <p><code>$</code> Données : <code>$table:portefeuille</code>, <code>$doc:…</code>, <code>$chart:…</code>, <code>$data:…</code>, <code>$metric:…</code> ; alias <code>$actif:PAAS</code>, <code>$broker:…</code>. Optionnel : <code>;date_debut-date_fin</code>.</p>
                <p><code>#</code> Filtres : <code>#Energie</code>, <code>#Minier</code>…</p>
              </>
            )}
          </div>
        )}
        {messages.map((msg, i) => {
          const isLastEmptyAssistantWhileStreaming =
            isStreaming && i === messages.length - 1 && msg.role === 'assistant' && !(msg.content || '').trim();
          if (isLastEmptyAssistantWhileStreaming) return null;
          return (
            <MessageBubble
              key={i}
              message={msg}
              providers={providerList}
              hideProviderLabel={isStreaming && i === messages.length - 1 && msg.role === 'assistant'}
              oliverName={oliverName}
            />
          );
        })}
        {isStreaming && (
          <div className="bpm-ai-typing">
            <span className="bpm-ai-typing-dot" /><span className="bpm-ai-typing-dot" /><span className="bpm-ai-typing-dot" />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="bpm-ai-chat-input-wrap" style={isMobile ? { bottom: 60 + keyboardOffset } : undefined}>
        {/* Masquer uniquement la zone bordée (input) pendant "Chargement des IA…", pas pendant le spinner de réponse (isStreaming) : garder la zone de saisie visible quand le spinner charge. */}
        {!showProviderTooltipMessage && (
        <div ref={inputRowRef} className="bpm-ai-chat-input-row">
          <div className="bpm-ai-chat-input-box">
            {attachedFiles.length > 0 && (
              <div className="bpm-ai-chat-attached-row">
                {attachedFiles.map((file, index) => (
                  <span key={`${file.name}-${index}`} className="bpm-ai-chat-attached-chip">
                    <span className="bpm-ai-chat-attached-name" title={file.name}>{file.name}</span>
                    <button
                      type="button"
                      className="bpm-ai-chat-attached-remove"
                      onClick={() => removeAttachedFile(index)}
                      aria-label={`Retirer ${file.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          <div className="bpm-ai-chat-input-wrap-inner">
            {/* Overlay : même texte que le textarea avec @ et $ colorés (derrière le textarea) */}
            {inputText && (
              <div
                ref={highlightRef}
                className="bpm-ai-chat-input-highlight"
                aria-hidden
              >
                <div className="bpm-ai-chat-input-highlight-inner">
                  {highlightAtAndDollar(inputText)}
                </div>
              </div>
            )}
            <textarea
              ref={inputRef}
              className={`bpm-ai-chat-input${inputText ? ' bpm-ai-chat-input--highlight' : ''}`}
              placeholder={!isAuthenticated ? `Connectez-vous pour discuter avec ${oliverName}…` : (messages.length === 0 ? `Discuter avec ${oliverName}…` : `Répondre à ${oliverName}…`)}
              value={inputText}
              onChange={handleInputChange}
              onScroll={() => {
                if (highlightRef.current && inputRef.current) {
                  highlightRef.current.scrollTop = inputRef.current.scrollTop;
                  highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isStreaming || !isAuthenticated}
              rows={1}
            />
            {/* Boutons + et Envoyer uniquement ; pendant le streaming les deux restent affichés mais désactivés (pas de bouton Annuler) */}
            <div className="bpm-ai-chat-input-actions">
              <Tooltip title="Joindre des fichiers" placement="top">
                <span>
                  <button
                    type="button"
                    className="bpm-ai-chat-add-files"
                    onClick={() => !isStreaming && fileInputRef.current?.click()}
                    disabled={isStreaming || !isAuthenticated}
                    aria-label="Joindre des fichiers"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/>
                    </svg>
                  </button>
                </span>
              </Tooltip>
              <Tooltip title="Envoyer" placement="top">
                <span>
                  <button
                    type="button"
                    className="bpm-ai-chat-send-arrow"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isStreaming) return;
                      if (inputText.trim()) handleSend();
                    }}
                    disabled={isStreaming || !isAuthenticated || !inputText.trim()}
                    aria-label="Envoyer"
                  >
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" style={{ pointerEvents: 'none' }}>
                  <path d="M460-200v-483.15L228.31-451.46 200-480l280-280 280 280-28.31 28.54L500-683.15V-200h-40Z"/>
                </svg>
                  </button>
                </span>
              </Tooltip>
            </div>
          </div>
          </div>
        </div>
        )}
        {(hasProviderSugg || showProviderTooltipMessage || showTokenSugg) && (
          <div
            className="bpm-ai-suggestions-tooltip"
            role="listbox"
            style={tooltipWidth != null ? { width: tooltipWidth, maxWidth: tooltipWidth } : undefined}
          >
            {showProviderTooltipMessage && (
              <div className="bpm-ai-sugg-tooltip-item bpm-ai-sugg-tooltip-message" role="status">
                {providersLoadFailed
                  ? 'Erreur d\'authentification (401). Rafraîchissez la page ou reconnectez-vous.'
                  : 'Chargement des IA…'}
              </div>
            )}
            {hasProviderSugg && atFilteredList.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  className="bpm-ai-sugg-tooltip-item"
                  onClick={() => {
                    const v = inputText.replace(/@\w*$/, '@' + p.shortLabel + ' ');
                    setInputText(v);
                    setShowProviderSugg(false);
                    inputRef.current?.focus();
                  }}
                >
                  <span className="bpm-ai-sugg-tooltip-content">
                    <span className="bpm-ai-sugg-tooltip-primary">{p.shortLabel}</span>
                    <span className="bpm-ai-sugg-tooltip-secondary">@{p.shortLabel}</span>
                </span>
              </button>
            ))}
            {showTokenSugg && tokenSuggestions.slice(0, 14).map((s, i) => (
              <button
                key={`${s.token}-${i}`}
                type="button"
                role="option"
                className="bpm-ai-sugg-tooltip-item"
                onClick={() => {
                  const re = s.isSub ? /\$\w+(?::[\w-]*)?$/ : /\$(\w*)$/;
                  const v = inputText.replace(re, s.token + ' ');
                  setInputText(v);
                  setShowTokenSugg(false);
                  inputRef.current?.focus();
                }}
              >
                <span className="bpm-ai-sugg-tooltip-content">
                  <span className="bpm-ai-sugg-tooltip-primary">{s.label}</span>
                  <span className="bpm-ai-sugg-tooltip-secondary" title={s.location}>
                    {s.location ? `${s.token} · ${s.location}` : s.token}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
